// src/components/PurchaseFlow/Steps/DataEntry/Sections/PersonalInfo.js
import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import { faCheckCircle, faExclamationTriangle, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PersonalInfo = forwardRef((props, ref) => {
  const {
    formData,
    handleChange,
    activeSection,
    sectionName,
    sectionValidity,
    setSectionValidity,
    handleSectionClick,
    displayMode = 'accordion'
  } = props;

  const isContinuousMode = displayMode === 'continuous';

  const [errors, setErrors] = useState({});
  const [allowedEditFields] = useState(() => {
    const envValue = process.env.REACT_APP_ALLOW_EDIT_PERSONAL_INFO;
    if (!envValue || envValue === 'true' || envValue === 'false') {
      return [];
    }
    return envValue.split(',').map(field => field.trim());
  });

  const KNOWN_GENDER_CODES = new Set(['M', 'F', 'X']);

  const getFieldError = (field, value) => {
    switch (field) {
      case 'firstName':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return 'El nombre es requerido';
        }
        return '';
      case 'lastName':
        if (!value || (typeof value === 'string' && !value.trim())) {
          return 'El apellido es requerido';
        }
        return '';
      case 'gender': {
        const norm = typeof value === 'string' ? value.trim().toUpperCase() : '';
        if (!norm || !KNOWN_GENDER_CODES.has(norm)) {
          return 'El sexo es requerido';
        }
        return '';
      }
      case 'birthDate':
        if (!value) {
          return 'La fecha de nacimiento es requerida';
        }
        return '';
      default:
        return '';
    }
  };

  const buildErrorsFromFormData = (data) => {
    const fields = ['firstName', 'lastName', 'gender', 'birthDate'];
    const newErrors = {};
    fields.forEach((field) => {
      const msg = getFieldError(field, data[field]);
      if (msg) newErrors[field] = msg;
    });
    return newErrors;
  };

  const validateField = (name, value) => {
    const merged = { ...formData, [name]: value };
    const newErrors = buildErrorsFromFormData(merged);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
  };

  const validateSection = () => {
    const newErrors = buildErrorsFromFormData(formData);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    setSectionValidity((prev) => ({ ...prev, [sectionName]: isValid }));
    return isValid;
  };

  useEffect(() => {
    validateSection();
  }, [formData.firstName, formData.lastName, formData.gender, formData.birthDate]);

  const isEmptyForEditing = (value, fieldName) => {
    if (fieldName === 'gender') {
      if (value == null) return true;
      if (typeof value !== 'string') return true;
      const normalized = value.trim().toUpperCase();
      if (normalized === '') return true;
      return !KNOWN_GENDER_CODES.has(normalized);
    }
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
  };

  const isFieldEditable = (fieldValue, fieldName) => {
    if (allowedEditFields.includes(fieldName)) {
      return true;
    }
    return isEmptyForEditing(fieldValue, fieldName);
  };

  const isGenderSelectEditable = () => {
    if (allowedEditFields.includes('gender')) {
      return true;
    }
    return isEmptyForEditing(formData.gender, 'gender');
  };

  useImperativeHandle(ref, () => ({
    validateSection,
  }));

  // Renderizado condicional basado en el modo de visualización
  if (isContinuousMode) {
    return (
      <div className="continuous-section mb-2">
        <div className="section-header mb-3">
          <h5 className="d-flex align-items-center mb-0 fw-light">
            <FontAwesomeIcon icon={faUser} className="me-2" />
            Datos Personales
            {sectionValidity[sectionName] === true && (
              <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
            )}
            {sectionValidity[sectionName] === false && (
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
            )}
          </h5>
        </div>
        <div className="section-content">
          <div className="row">
            {/* Campos del formulario con validación */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Nombre<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.firstName, 'firstName')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                placeholder="Ingrese su nombre"
                autoComplete="off"
                required
              />
              {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Apellido<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.lastName, 'lastName')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                placeholder="Ingrese su apellido"
                autoComplete="off"
                required
              />
              {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Sexo<sup>*</sup></label>
              <select
                className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
                disabled={!isGenderSelectEditable()}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                required
                autoComplete="off"
              >
                <option value="" disabled>Seleccione</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
                <option value="X">Otro</option>
              </select>
              {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Fecha de Nacimiento<sup>*</sup></label>
              <input
                type="date"
                className={`form-control ${errors.birthDate ? 'is-invalid' : ''}`}
                name="birthDate"
                value={formData.birthDate || ''}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.birthDate, 'birthDate')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                required
                autoComplete="off"
              />
              {errors.birthDate && <div className="invalid-feedback">{errors.birthDate}</div>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Modo accordion (original)
  return (
    <div className="accordion-item">
      <h2 className="accordion-header" id={`heading${sectionName}`}>
        <button
          className={`accordion-button ${activeSection !== sectionName ? 'collapsed' : ''}`}
          type="button"
          onClick={() => handleSectionClick(sectionName)}
        >
          <FontAwesomeIcon icon={faUser} className="me-2" />
          Datos Personales{' '}
          {sectionValidity[sectionName] === true && (
            <FontAwesomeIcon icon={faCheckCircle} className="text-success ms-2" />
          )}
          {sectionValidity[sectionName] === false && (
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger ms-2" />
          )}
        </button>
      </h2>
      <div
        className={`accordion-collapse collapse ${activeSection === sectionName ? 'show' : ''}`}
        aria-labelledby={`heading${sectionName}`}
      >
        <div className="accordion-body">
          <div className="row">
            {/* Campos del formulario con validación */}
            <div className="col-md-6 mb-3">
              <label className="form-label">Nombre<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.firstName, 'firstName')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                placeholder="Ingrese su nombre"
                autoComplete="off"
                required
              />
              {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Apellido<sup>*</sup></label>
              <input
                type="text"
                className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.lastName, 'lastName')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                placeholder="Ingrese su apellido"
                autoComplete="off"
                required
              />
              {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Sexo<sup>*</sup></label>
              <select
                className={`form-control ${errors.gender ? 'is-invalid' : ''}`}
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
                disabled={!isGenderSelectEditable()}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                required
                autoComplete="off"
              >
                <option value="" disabled>Seleccione</option>
                <option value="M">Masculino</option>
                <option value="F">Femenino</option>
                <option value="X">Otro</option>
              </select>
              {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">Fecha de Nacimiento<sup>*</sup></label>
              <input
                type="date"
                className={`form-control ${errors.birthDate ? 'is-invalid' : ''}`}
                name="birthDate"
                value={formData.birthDate || ''}
                onChange={handleChange}
                disabled={!isFieldEditable(formData.birthDate, 'birthDate')}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                required
                autoComplete="off"
              />
              {errors.birthDate && <div className="invalid-feedback">{errors.birthDate}</div>}
            </div>
         </div>
        </div>
      </div>
    </div>
  );
});

export default PersonalInfo;
